import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { MEDIA_TYPES, PROJECT_TYPES, REACTIONS } from '../../../../constants';
import Activ8Image from '../../../components/content-media/image/Activ8Image';
import Activ8Video from '../../../components/content-media/video/Activ8Video';
import ContentSlideActions from '../../../components/content-slide-actions/ContentSlideActions';
import { ProjectProvider, useProject } from '../../../../context/ProjectContext';
import downloadFile from '../../../../utils/downloadFile';
import { updateBucketItem } from '../../../../redux/ai-studio/aiStudioReducer';
import CommonModal from '../../../../components/common-modal/CommonModal';
import useCommonModal from '../../../../hooks/useCommonModal';
import EditTagsModal from '../../../components/edit-tags-modal/EditTagsModal';
import { deleteActiv8Content } from '../../../../redux/activ8/activ8Actions';
import { MEDIA_LINK_VARIANTS, resolveActiv8MediaLink } from '../../../utils/resolveActiv8MediaLink';

import scss from './MediaSelectSlide.module.scss';

function ServerMediaSlide({ slide }) {
  const dispatch = useDispatch();
  const { actions } = useProject();
  const [isError, setIsError] = useState(false);
  const { isOpen, handleOpenModal, handleCloseModal } = useCommonModal({ initialState: false });
  const { isOpen: isEditTagsModalOpen, handleCloseModal: handleCloseEditTagsModal } = useCommonModal({
    initialState: false,
  });
  const { converted, fileName, contentDownloadLink } = slide;
  const { projectVariant } = useProject();
  const { id: projectId } = useParams();
  const { src, poster, backgroundSrc } = resolveActiv8MediaLink(slide, MEDIA_LINK_VARIANTS.DISPLAY);

  useEffect(() => {
    setIsError(false);
  }, [slide.id, src, poster]);

  const handleMediaError = () => {
    setIsError(true);
  };

  const handlePreview = () => {
    handleOpenModal();
  };

  const handleLike = (slide) => {
    const newReaction = slide.reaction === REACTIONS.LIKE ? '' : REACTIONS.LIKE;
    actions('likeContent', { id: slide.id, reaction: newReaction });

    dispatch(updateBucketItem({ id: slide.id, newContent: { reaction: newReaction } }));
  };

  const handleDelete = async (slide) => {
    if (slide.isCustom) {
      return;
    }

    await dispatch(deleteActiv8Content({ contentId: slide.id, projectId }));
  };

  const handleDownload = () => {
    if (contentDownloadLink) {
      downloadFile(contentDownloadLink, fileName);
    }
  };

  const handleDownloadSource = async ({ id }) => {
    const {
      payload: { file, fileName },
    } = await actions('downloadSource', { id });

    if (file) {
      downloadFile(file, fileName);
    }
  };

  const mappedActionsByType = {
    [MEDIA_TYPES.IMAGE]: {
      onLike: handleLike,
      onDownload: handleDownload,
      // onEditTags: handleEditTags,
      onDelete: handleDelete,
      ...(converted ? { onDownloadSource: handleDownloadSource } : {}),
    },
    [MEDIA_TYPES.VIDEO]: {
      onLike: handleLike,
      // onEditTags: handleEditTags,
      onDelete: handleDelete,
    },
  };

  return (
    <div className={clsx(scss.wrapper)}>
      {slide.mediaType === MEDIA_TYPES.IMAGE && (
        <Activ8Image
          image={slide}
          src={src}
          backgroundSrc={backgroundSrc}
          onError={handleMediaError}
          onPreview={handlePreview}
          backgroundImageStyle={{ borderTopRightRadius: '0.5rem', borderBottomRightRadius: '0.5rem' }}
        />
      )}
      {slide.mediaType === MEDIA_TYPES.VIDEO && <Activ8Video src={src} poster={poster} onError={handleMediaError} />}
      <ContentSlideActions slide={slide} isError={isError} {...mappedActionsByType[slide.mediaType]} />
      <PreviewModal isOpen={isOpen} onClose={handleCloseModal} slide={slide} />
      <ProjectProvider projectType={PROJECT_TYPES.ACTIV8} projectVariant={projectVariant}>
        <EditTagsModal
          isOpen={isEditTagsModalOpen}
          onClose={handleCloseEditTagsModal}
          tags={slide.tags}
          contentId={slide.id}
        />
      </ProjectProvider>
    </div>
  );
}

export default ServerMediaSlide;

const PreviewModal = ({ isOpen, onClose, slide }) => {
  const { src, backgroundSrc } = resolveActiv8MediaLink(slide, MEDIA_LINK_VARIANTS.DISPLAY);

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} isAnimated>
      <div className={scss.previewModal}>
        <Activ8Image
          image={slide}
          src={src}
          backgroundSrc={backgroundSrc}
          onError={() => {}}
          imageClassName={scss.previewImage}
        />
      </div>
    </CommonModal>
  );
};
